import React, { useState, useEffect } from 'react';
import { Search, Cloud, Thermometer, MapPin, Users, Calendar, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface WeatherAlert {
    id: number;
    sender_id: number;
    sender_username: string;
    sender_name?: string;
    sender_avatar?: string;
    family_id: number;
    family_name: string;
    alert_type: string;
    temperature?: number;
    latitude?: number;
    longitude?: number;
    message?: string;
    devices_sent: number;
    created_at: string;
}

interface AlertStats {
    total: number;
    today: number;
    by_type: { rain: number; snow: number; storm: number; extreme_heat: number; extreme_cold: number };
}

const WeatherAlertManagement: React.FC = () => {
    const [alerts, setAlerts] = useState<WeatherAlert[]>([]);
    const [stats, setStats] = useState<AlertStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setRefreshing(true);
        try {
            const [alertsRes, statsRes] = await Promise.all([
                axios.get('https://api.lifeshare.app/api/v1/admin/weather-alerts'),
                axios.get('https://api.lifeshare.app/api/v1/admin/weather-alerts/stats')
            ]);
            setAlerts(alertsRes.data.alerts || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const getAlertBadge = (type: string) => {
        const configs: Record<string, { emoji: string; label: string; bg: string; text: string }> = {
            rain: { emoji: '🌧️', label: 'Yağmur', bg: 'bg-blue-100', text: 'text-blue-700' },
            snow: { emoji: '❄️', label: 'Kar', bg: 'bg-cyan-100', text: 'text-cyan-700' },
            storm: { emoji: '⛈️', label: 'Fırtına', bg: 'bg-purple-100', text: 'text-purple-700' },
            extreme_heat: { emoji: '🌡️', label: 'Sıcak', bg: 'bg-red-100', text: 'text-red-700' },
            extreme_cold: { emoji: '🥶', label: 'Soğuk', bg: 'bg-indigo-100', text: 'text-indigo-700' }
        };
        const c = configs[type] || { emoji: '🌡️', label: type, bg: 'bg-gray-100', text: 'text-gray-700' };
        return <span className={`px-2 py-1 ${c.bg} ${c.text} rounded-xl text-xs font-medium`}>{c.emoji} {c.label}</span>;
    };

    const filteredAlerts = alerts.filter(a =>
        (a.sender_username || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (a.sender_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (a.family_name || '').toLowerCase().includes(searchText.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Hava Durumu Bildirimleri</h1>
                        <p className="text-sm text-gray-500 mt-1">Gönderilen hava durumu uyarılarını görüntüle</p>
                    </div>
                    <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-2">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-3">
                            <div className="flex items-center gap-1 mb-1 opacity-70"><Cloud className="w-3 h-3" /></div>
                            <p className="text-lg font-bold text-purple-700">{stats.total}</p>
                            <p className="text-xs text-purple-600">Toplam</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-3">
                            <div className="flex items-center gap-1 mb-1 opacity-70"><Calendar className="w-3 h-3" /></div>
                            <p className="text-lg font-bold text-green-700">{stats.today}</p>
                            <p className="text-xs text-green-600">Bugün</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-3">
                            <p className="text-xs text-blue-600">🌧️ Yağmur</p>
                            <p className="text-lg font-bold text-blue-700">{stats.by_type.rain}</p>
                        </div>
                        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-2xl p-3">
                            <p className="text-xs text-cyan-600">❄️ Kar</p>
                            <p className="text-lg font-bold text-cyan-700">{stats.by_type.snow}</p>
                        </div>
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-3">
                            <p className="text-xs text-purple-600">⛈️ Fırtına</p>
                            <p className="text-lg font-bold text-purple-700">{stats.by_type.storm}</p>
                        </div>
                        <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-3">
                            <p className="text-xs text-red-600">🌡️ Sıcak</p>
                            <p className="text-lg font-bold text-red-700">{stats.by_type.extreme_heat}</p>
                        </div>
                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-2xl p-3">
                            <p className="text-xs text-indigo-600">🥶 Soğuk</p>
                            <p className="text-lg font-bold text-indigo-700">{stats.by_type.extreme_cold}</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı veya çevre ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* List */}
                {filteredAlerts.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Cloud className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Hava durumu uyarısı bulunamadı</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredAlerts.map((alert) => (
                            <div key={alert.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-start gap-3 mb-3">
                                    {alert.sender_avatar ? (
                                        <img src={`https://api.lifeshare.app${alert.sender_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                                            <Cloud className="w-5 h-5" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{alert.sender_name || alert.sender_username}</p>
                                        <p className="text-xs text-gray-500">@{alert.sender_username}</p>
                                    </div>
                                    {getAlertBadge(alert.alert_type)}
                                </div>

                                <div className="space-y-2 text-xs">
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Users className="w-3 h-3" />
                                        <span>Çevre: <strong>{alert.family_name}</strong></span>
                                        <span className="text-green-600 ml-auto">📱 {alert.devices_sent} cihaz</span>
                                    </div>

                                    {alert.temperature && (
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <Thermometer className="w-3 h-3" />
                                            <span>Sıcaklık: <strong>{alert.temperature.toFixed(0)}°C</strong></span>
                                        </div>
                                    )}

                                    {alert.latitude && alert.longitude && (
                                        <div className="flex items-center gap-2 text-gray-600">
                                            <MapPin className="w-3 h-3" />
                                            <span className="font-mono text-xs">{alert.latitude.toFixed(4)}, {alert.longitude.toFixed(4)}</span>
                                        </div>
                                    )}

                                    {alert.message && (
                                        <div className="p-2 bg-gray-50 rounded-xl text-gray-700">"{alert.message}"</div>
                                    )}

                                    <div className="text-right text-gray-400">{new Date(alert.created_at).toLocaleString('tr-TR')}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeatherAlertManagement;
